/**
 * 정적 추천 매핑 — 상위 선택(작업 종류·장소) 으로 하위 객관식 옵션을 자동 추천.
 *
 * AI 콜 X. 산업안전 표준 + 산안법 §38·§42·§44 위험작업 분류 기반 휴리스틱.
 *
 * 사용:
 *   const rec = mergeRecommendations(workType, workLocation)
 *   <CheckboxGroupWithCustom recommendedKeys={rec.conditions} ... />
 */

export interface RecommendationSet {
  conditions?: string[]   // WORK_CONDITION_OPTIONS values
  equipment?: string[]    // EQUIPMENT_OPTIONS values
  hazards?: string[]      // HAZARD_CATEGORY_OPTIONS values
  ppe?: string[]          // PPE_OPTIONS values
  safeguards?: string[]   // SAFEGUARD_OPTIONS values
  injuryTypes?: string[]  // INJURY_TYPE_OPTIONS values (재해조사용)
  bodyParts?: string[]    // BODY_PART_OPTIONS values
}

// ── 작업 종류별 추천 — 산안법 위험작업 + 일반 산업안전 표준 ────────────
export const WORK_TYPE_RECOMMENDATIONS: Record<string, RecommendationSet> = {
  '정비·보수': {
    conditions: ['중량물 취급', '소음 (85dB+)'],
    equipment: ['그라인더', '드릴', '체인블록'],
    hazards: ['협착', '낙하·비래', '근골격계'],
    ppe: ['안전모', '안전화', '보안경', '용접장갑'],
    safeguards: ['전원 차단(LOTO)', '작업 전 위험성평가 실시', 'TBM (작업 전 안전점검회의)'],
    injuryTypes: ['타박상', '베임·찢김', '협착'],
    bodyParts: ['손·손가락', '팔', '머리'],
  },
  '점검': {
    conditions: ['단독작업'],
    equipment: ['가스감지기'],
    hazards: ['전도', '추락'],
    ppe: ['안전모', '안전화'],
    safeguards: ['작업 전 위험성평가 실시', '교육·자격 확인'],
    bodyParts: ['다리', '허리', '머리'],
  },
  '청소': {
    conditions: ['협소공간'],
    equipment: [],
    hazards: ['전도', '화학물질 노출'],
    ppe: ['방진마스크', '안전화', '보안경'],
    safeguards: ['환기 설비 가동'],
    bodyParts: ['다리', '허리', '손·손가락'],
  },
  '운반·하역': {
    conditions: ['중량물 취급'],
    equipment: ['지게차', '체인블록', '크레인'],
    hazards: ['협착', '낙하·비래', '충돌', '근골격계'],
    ppe: ['안전모', '안전화'],
    safeguards: ['작업구역 출입통제', '감시인 배치'],
    injuryTypes: ['타박상', '근골격계 질환', '골절'],
    bodyParts: ['허리', '발·발가락', '손·손가락', '다리'],
  },
  '설치·시공': {
    conditions: ['고소작업', '중량물 취급'],
    equipment: ['비계', '사다리', '고소작업대', '크레인'],
    hazards: ['추락', '낙하·비래', '협착'],
    ppe: ['안전모', '안전화', '안전대'],
    safeguards: ['안전난간·낙하방지망 설치', '안전대 착용 및 부착설비 사용', '작업허가서 발행'],
    bodyParts: ['머리', '허리', '다리', '손·손가락'],
  },
  '해체·철거': {
    conditions: ['고소작업', '중량물 취급', '협소공간'],
    equipment: ['비계', '굴착기', '체인블록'],
    hazards: ['붕괴', '낙하·비래', '추락'],
    ppe: ['안전모', '안전화', '방진마스크', '보안경'],
    safeguards: ['작업구역 출입통제', '작업허가서 발행', '안전난간·낙하방지망 설치'],
    bodyParts: ['머리', '전신', '다리'],
  },
  '용접·절단': {
    conditions: ['화기작업', '고온', '소음 (85dB+)'],
    equipment: ['용접기', '소화기', '방진마스크'],
    hazards: ['화재·폭발', '고온', '소음'],
    ppe: ['용접장갑', '보안면', '방진마스크', '안전화'],
    safeguards: ['화기작업 시 소화기 비치', '작업허가서 발행', '환기 설비 가동'],
    injuryTypes: ['화상'],
    bodyParts: ['눈', '얼굴', '손·손가락', '팔'],
  },
  '도장': {
    conditions: ['유해화학물질', '협소공간'],
    equipment: ['방진마스크'],
    hazards: ['화학물질 노출', '화재·폭발'],
    ppe: ['방독마스크', '내화학복', '보안경', '안전화'],
    safeguards: ['환기 설비 가동', '가스농도 측정'],
    bodyParts: ['눈', '얼굴', '내부 장기'],
  },
  '굴착': {
    conditions: ['굴착작업'],
    equipment: ['굴착기'],
    hazards: ['붕괴', '낙하·비래', '추락'],
    ppe: ['안전모', '안전화'],
    safeguards: ['작업구역 출입통제', '감시인 배치'],
    bodyParts: ['전신', '머리', '다리'],
  },
  '운전·조작': {
    conditions: ['소음 (85dB+)'],
    equipment: ['비상정지장치'],
    hazards: ['협착', '감전'],
    ppe: ['안전모', '안전화', '귀마개'],
    safeguards: ['교육·자격 확인', '비상연락 체계 확보'],
    bodyParts: ['손·손가락', '팔'],
  },
  '전기작업': {
    conditions: ['활선 작업', '정전기 위험'],
    equipment: ['비상정지장치'],
    hazards: ['감전', '화재·폭발'],
    ppe: ['내전압장갑', '안전화', '보안경', '안전모'],
    safeguards: ['전원 차단(LOTO)', '작업구역 출입통제', '교육·자격 확인'],
    injuryTypes: ['감전상', '화상'],
    bodyParts: ['손·손가락', '팔', '전신'],
  },
  '고소작업': {
    conditions: ['고소작업', '강풍·우천'],
    equipment: ['비계', '사다리', '고소작업대', '안전벨트'],
    hazards: ['추락', '낙하·비래'],
    ppe: ['안전대', '안전모', '안전화'],
    safeguards: ['안전난간·낙하방지망 설치', '안전대 착용 및 부착설비 사용', '작업허가서 발행'],
    injuryTypes: ['골절', '뇌진탕', '복합 외상'],
    bodyParts: ['머리', '허리', '다리', '전신'],
  },
  '밀폐공간 작업': {
    conditions: ['밀폐공간', '협소공간'],
    equipment: ['가스감지기', '방진마스크'],
    hazards: ['산소결핍', '화학물질 노출'],
    ppe: ['송기마스크', '안전대'],
    safeguards: ['가스농도 측정', '환기 설비 가동', '감시인 배치', '작업허가서 발행'],
    injuryTypes: ['질식', '중독'],
    bodyParts: ['내부 장기', '전신', '머리'],
  },
  '크레인 작업': {
    conditions: ['중량물 취급'],
    equipment: ['크레인', '체인블록'],
    hazards: ['낙하·비래', '협착', '충돌'],
    ppe: ['안전모', '안전화'],
    safeguards: ['작업구역 출입통제', '감시인 배치', '교육·자격 확인'],
    bodyParts: ['머리', '발·발가락', '손·손가락'],
  },
  '지게차 작업': {
    conditions: ['중량물 취급'],
    equipment: ['지게차'],
    hazards: ['충돌', '협착', '낙하·비래'],
    ppe: ['안전모', '안전화'],
    safeguards: ['작업구역 출입통제', '교육·자격 확인'],
    bodyParts: ['다리', '발·발가락', '허리'],
  },
  '중량물 취급': {
    conditions: ['중량물 취급'],
    equipment: ['체인블록', '지게차'],
    hazards: ['근골격계', '협착', '낙하·비래'],
    ppe: ['안전모', '안전화'],
    safeguards: ['교육·자격 확인'],
    injuryTypes: ['근골격계 질환', '타박상'],
    bodyParts: ['허리', '손·손가락', '발·발가락', '어깨'],
  },
  '화학물질 취급': {
    conditions: ['유해화학물질'],
    equipment: ['가스감지기', '방진마스크', '소화기'],
    hazards: ['화학물질 노출', '화재·폭발'],
    ppe: ['방독마스크', '내화학복', '보안경'],
    safeguards: ['환기 설비 가동', '가스농도 측정', '작업허가서 발행'],
    injuryTypes: ['중독', '화상'],
    bodyParts: ['눈', '얼굴', '내부 장기', '손·손가락'],
  },
  '방사선 작업': {
    conditions: [],
    equipment: [],
    hazards: ['방사선'],
    ppe: ['보안경', '안전모'],
    safeguards: ['작업구역 출입통제', '교육·자격 확인', '작업허가서 발행'],
    bodyParts: ['전신', '내부 장기'],
  },
  '시운전': {
    conditions: [],
    equipment: ['비상정지장치'],
    hazards: ['감전', '협착', '화재·폭발'],
    ppe: ['안전모', '안전화', '보안경'],
    safeguards: ['작업구역 출입통제', '비상연락 체계 확보'],
    bodyParts: ['손·손가락', '얼굴'],
  },
}

// ── 작업 장소별 추천 ──────────────────────────────────────────────────────
export const LOCATION_RECOMMENDATIONS: Record<string, RecommendationSet> = {
  '공장동 내부': {
    conditions: ['소음 (85dB+)'],
    hazards: ['협착', '충돌', '소음'],
    ppe: ['안전모', '안전화', '귀마개'],
    bodyParts: ['손·손가락', '귀'],
  },
  '옥외 작업장': {
    conditions: ['강풍·우천'],
    hazards: ['전도', '추락'],
    ppe: ['안전모', '안전화'],
    bodyParts: ['다리', '머리', '허리'],
  },
  '저장탱크 구역': {
    conditions: ['밀폐공간', '유해화학물질'],
    equipment: ['가스감지기'],
    hazards: ['화재·폭발', '화학물질 노출', '산소결핍'],
    ppe: ['방독마스크', '내화학복'],
    safeguards: ['가스농도 측정', '환기 설비 가동', '작업허가서 발행'],
    bodyParts: ['내부 장기', '얼굴', '전신'],
  },
  '반응기실': {
    conditions: ['유해화학물질', '고온'],
    equipment: ['가스감지기', '소화기'],
    hazards: ['화재·폭발', '화학물질 노출', '고온'],
    ppe: ['방독마스크', '내화학복', '보안면'],
    safeguards: ['가스농도 측정', '환기 설비 가동', '작업허가서 발행'],
    bodyParts: ['얼굴', '눈', '내부 장기'],
  },
  '보일러실': {
    conditions: ['고온', '협소공간'],
    equipment: ['소화기'],
    hazards: ['고온', '화재·폭발', '산소결핍'],
    ppe: ['보안면', '용접장갑'],
    safeguards: ['가스농도 측정', '환기 설비 가동'],
    injuryTypes: ['화상'],
    bodyParts: ['얼굴', '손·손가락', '팔'],
  },
  '변전실': {
    conditions: ['활선 작업'],
    hazards: ['감전', '화재·폭발'],
    ppe: ['내전압장갑', '안전화', '보안경'],
    safeguards: ['전원 차단(LOTO)', '작업구역 출입통제'],
    injuryTypes: ['감전상', '화상'],
    bodyParts: ['손·손가락', '팔', '전신'],
  },
  '폐수처리장': {
    conditions: ['유해화학물질', '밀폐공간'],
    equipment: ['가스감지기'],
    hazards: ['화학물질 노출', '산소결핍'],
    ppe: ['방독마스크', '내화학복', '안전화'],
    safeguards: ['가스농도 측정', '환기 설비 가동'],
    bodyParts: ['내부 장기', '얼굴', '눈'],
  },
  '저장창고': {
    conditions: ['중량물 취급'],
    equipment: ['지게차', '체인블록'],
    hazards: ['낙하·비래', '협착', '충돌'],
    ppe: ['안전모', '안전화'],
    safeguards: ['작업구역 출입통제'],
    bodyParts: ['발·발가락', '손·손가락', '머리'],
  },
  '하역장': {
    conditions: ['중량물 취급', '강풍·우천'],
    equipment: ['지게차', '크레인'],
    hazards: ['충돌', '낙하·비래', '협착'],
    ppe: ['안전모', '안전화'],
    safeguards: ['작업구역 출입통제', '감시인 배치'],
    bodyParts: ['다리', '허리', '머리'],
  },
  '옥상': {
    conditions: ['고소작업', '강풍·우천'],
    equipment: ['안전벨트'],
    hazards: ['추락'],
    ppe: ['안전대', '안전모', '안전화'],
    safeguards: ['안전난간·낙하방지망 설치', '안전대 착용 및 부착설비 사용'],
    bodyParts: ['머리', '허리', '다리', '전신'],
  },
  '지하 피트': {
    conditions: ['밀폐공간', '협소공간'],
    equipment: ['가스감지기'],
    hazards: ['산소결핍', '추락', '화학물질 노출'],
    ppe: ['송기마스크', '안전대'],
    safeguards: ['가스농도 측정', '환기 설비 가동', '감시인 배치', '작업허가서 발행'],
    bodyParts: ['내부 장기', '머리', '전신'],
  },
  '맨홀 내부': {
    conditions: ['밀폐공간', '협소공간'],
    equipment: ['가스감지기'],
    hazards: ['산소결핍', '화학물질 노출', '추락'],
    ppe: ['송기마스크', '안전대'],
    safeguards: ['가스농도 측정', '환기 설비 가동', '감시인 배치', '작업허가서 발행'],
    injuryTypes: ['질식', '중독'],
    bodyParts: ['내부 장기', '얼굴', '전신'],
  },
  '밀폐공간': {
    conditions: ['밀폐공간', '협소공간'],
    equipment: ['가스감지기'],
    hazards: ['산소결핍', '화학물질 노출'],
    ppe: ['송기마스크', '안전대'],
    safeguards: ['가스농도 측정', '환기 설비 가동', '감시인 배치', '작업허가서 발행'],
    injuryTypes: ['질식', '중독'],
    bodyParts: ['내부 장기', '전신'],
  },
  '굴착 작업장': {
    conditions: ['굴착작업'],
    equipment: ['굴착기'],
    hazards: ['붕괴', '낙하·비래', '추락'],
    ppe: ['안전모', '안전화'],
    safeguards: ['작업구역 출입통제', '감시인 배치'],
    bodyParts: ['전신', '머리', '다리'],
  },
  '비계·고소작업대': {
    conditions: ['고소작업'],
    equipment: ['비계', '고소작업대', '안전벨트'],
    hazards: ['추락', '낙하·비래'],
    ppe: ['안전대', '안전모', '안전화'],
    safeguards: ['안전난간·낙하방지망 설치', '안전대 착용 및 부착설비 사용', '작업허가서 발행'],
    injuryTypes: ['골절', '복합 외상'],
    bodyParts: ['머리', '허리', '다리'],
  },
}

/**
 * 두 추천 세트 합치기 — workType 우선 + location 보조 (중복 자동 제거).
 * 빈 키는 빈 배열 반환.
 */
function mergeArrays(...arrs: (string[] | undefined)[]): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const a of arrs) {
    if (!a) continue
    for (const v of a) {
      if (!seen.has(v)) {
        seen.add(v)
        out.push(v)
      }
    }
  }
  return out
}

export interface MergedRecommendation {
  conditions: string[]
  equipment: string[]
  hazards: string[]
  ppe: string[]
  safeguards: string[]
  injuryTypes: string[]
  bodyParts: string[]
  hasAny: boolean
}

/**
 * 핸드오프(RA→JSA, JSA→PTW)로 넘어온 작업 설명 텍스트에서 옵션 키워드를 찾아 체크할 항목을 반환.
 * 빈 폼처럼 보이는 혼란을 줄이기 위해 사전 선택용. (값의 첫 토큰이 텍스트에 포함되면 선택)
 */
export function selectMatchingOptions(text: string | undefined, options: readonly { value: string }[]): string[] {
  if (!text) return []
  const t = text.toLowerCase()
  const out: string[] = []
  for (const o of options) {
    const core = (o.value.split(/[\s(/·]/)[0] || '').trim().toLowerCase()
    if (core.length >= 2 && t.includes(core)) out.push(o.value)
  }
  return out
}

/** workType + location → 합쳐진 추천 (workType 우선). 둘 다 빈 값이면 hasAny=false. */
export function mergeRecommendations(workType?: string, location?: string): MergedRecommendation {
  const w = workType ? WORK_TYPE_RECOMMENDATIONS[workType] : undefined
  const l = location ? LOCATION_RECOMMENDATIONS[location] : undefined
  const merged: MergedRecommendation = {
    conditions: mergeArrays(w?.conditions, l?.conditions),
    equipment: mergeArrays(w?.equipment, l?.equipment),
    hazards: mergeArrays(w?.hazards, l?.hazards),
    ppe: mergeArrays(w?.ppe, l?.ppe),
    safeguards: mergeArrays(w?.safeguards, l?.safeguards),
    injuryTypes: mergeArrays(w?.injuryTypes, l?.injuryTypes),
    bodyParts: mergeArrays(w?.bodyParts, l?.bodyParts),
    hasAny: false,
  }
  merged.hasAny =
    merged.conditions.length > 0 || merged.equipment.length > 0 ||
    merged.hazards.length > 0 || merged.ppe.length > 0 ||
    merged.safeguards.length > 0 || merged.injuryTypes.length > 0 ||
    merged.bodyParts.length > 0
  return merged
}
